import React, { useEffect, useMemo, useState } from "react";
import styles from "./VideoTutorialOverlay.module.scss";

type TourStep = {
  key: string;
  selector: string;
  title: string;
  description: React.ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
};

interface VideoTutorialOverlayProps {
  open: boolean;
  onClose: () => void;
}

type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  radius: number;
};

const PADDING = 6;

const VideoTutorialOverlay: React.FC<VideoTutorialOverlayProps> = ({
  open,
  onClose,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const steps: TourStep[] = useMemo(
    () => [
      {
        key: "new-playlist",
        selector: '[data-tour="new-playlist-button"]',
        title: "Create playlist",
        description: "Create a playlist for your videos.",
        placement: "bottom",
      },
      {
        key: "browse-playlists",
        selector: '[data-tour="playlist-list"]',
        title: "Browse playlists",
        description: "Search, filter, and select a playlist.",
        placement: "right",
      },
      {
        key: "video-player",
        selector: '[data-tour="video-player"]',
        title: "Preview video",
        description: "Play the selected video here.",
        placement: "left",
      },
      {
        key: "manage-videos",
        selector: '[data-tour="video-list-card"]',
        title: "Manage videos",
        description: "Add, play, or remove playlist videos.",
        placement: "top",
      },
      {
        key: "visibility-and-actions",
        selector: '[data-tour="playlist-settings-card"]',
        title: "Visibility and actions",
        description: (
          <>
            <strong>Visibility:</strong> User only keeps the playlist private to
            the current user. Practice makes it available at practice level.
            <br />
            <strong>Actions:</strong> Save Changes updates playlist settings.
            Delete removes the playlist when deletion is allowed.
          </>
        ),
        placement: "top",
      },
    ],
    []
  );

  const currentStep = steps[activeStep];

  const updateSpotlight = () => {
    if (!currentStep) return;

    const target = document.querySelector(currentStep.selector) as HTMLElement | null;

    if (!target) {
      setSpotlightRect(null);
      return;
    }

    const rect = target.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(target);

    const targetRadius =
      parseFloat(computedStyle.borderTopLeftRadius || computedStyle.borderRadius || "12") || 12;

    setSpotlightRect({
      top: Math.round(rect.top - PADDING),
      left: Math.round(rect.left - PADDING),
      width: Math.round(rect.width + PADDING * 2),
      height: Math.round(rect.height + PADDING * 2),
      radius: Math.round(targetRadius + PADDING),
    });
  };

  const scrollToTarget = () => {
    if (!currentStep) return;

    const target = document.querySelector(currentStep.selector) as HTMLElement | null;

    if (!target) return;

    target.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  };

  useEffect(() => {
    if (!open) return;

    scrollToTarget();

    const timer = window.setTimeout(updateSpotlight, 180);

    const handleLayoutChange = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

      updateSpotlight();
    };

    window.addEventListener("resize", handleLayoutChange);
    window.addEventListener("scroll", updateSpotlight, true);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", handleLayoutChange);
      window.removeEventListener("scroll", updateSpotlight, true);
    };
  }, [open, activeStep, currentStep]);

  useEffect(() => {
    if (open) {
      setActiveStep(0);
    }
  }, [open]);

  if (!open || !currentStep) return null;

  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;

  const getTooltipPosition = () => {
    if (!spotlightRect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const tooltipWidth = 400;
    const tooltipHeight = 235;
    const spacing = 16;

    let top = spotlightRect.top + spotlightRect.height + spacing;
    let left = spotlightRect.left;

    if (currentStep.placement === "top") {
      top = spotlightRect.top - tooltipHeight - spacing;
      left = spotlightRect.left;
    }

    if (currentStep.placement === "left") {
      top = spotlightRect.top;
      left = spotlightRect.left - tooltipWidth - spacing;
    }

    if (currentStep.placement === "right") {
      top = spotlightRect.top;
      left = spotlightRect.left + spotlightRect.width + spacing;
    }

    const safeTop = Math.max(18, Math.min(top, window.innerHeight - tooltipHeight - 18));
    const safeLeft = Math.max(18, Math.min(left, window.innerWidth - tooltipWidth - 18));

    return {
      top: `${safeTop}px`,
      left: `${safeLeft}px`,
      transform: "none",
    };
  };

  const handleNext = () => {
    if (isLast) {
      onClose();
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (isFirst) return;

    setActiveStep((prev) => prev - 1);
  };

  return (
    <div className={styles.tourRoot}>
      {spotlightRect ? (
        <>
          <svg
            className={styles.overlaySvg}
            width={viewportSize.width}
            height={viewportSize.height}
            viewBox={`0 0 ${viewportSize.width} ${viewportSize.height}`}
            preserveAspectRatio="none"
          >
            <defs>
              <mask id="video-tour-cutout-mask">
                <rect
                  x="0"
                  y="0"
                  width={viewportSize.width}
                  height={viewportSize.height}
                  fill="white"
                />

                <rect
                  x={spotlightRect.left}
                  y={spotlightRect.top}
                  width={spotlightRect.width}
                  height={spotlightRect.height}
                  rx={spotlightRect.radius}
                  ry={spotlightRect.radius}
                  fill="black"
                />
              </mask>
            </defs>

            <rect
              x="0"
              y="0"
              width={viewportSize.width}
              height={viewportSize.height}
              className={styles.overlayFill}
              mask="url(#video-tour-cutout-mask)"
            />
          </svg>

          <div
            className={styles.spotlight}
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left,
              width: spotlightRect.width,
              height: spotlightRect.height,
              borderRadius: spotlightRect.radius,
            }}
          />
        </>
      ) : (
        <div className={styles.fullLayer} />
      )}

      <div className={styles.tooltipCard} style={getTooltipPosition()}>
        <div className={styles.stepBadge}>
          Step {activeStep + 1} of {steps.length}
        </div>

        <h2>{currentStep.title}</h2>

        <p>{currentStep.description}</p>

        <div className={styles.progressTrack}>
          <span
            style={{
              width: `${((activeStep + 1) / steps.length) * 100}%`,
            }}
          />
        </div>

        <div className={styles.footerActions}>
          <button type="button" className={styles.skipBtn} onClick={onClose}>
            Skip
          </button>

          <div className={styles.rightActions}>
            <button
              type="button"
              className={styles.backBtn}
              onClick={handleBack}
              disabled={isFirst}
            >
              Back
            </button>

            <button type="button" className={styles.nextBtn} onClick={handleNext}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTutorialOverlay;
